import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function TenantCampaignPresets() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/campaign-presets");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const install = async (preset) => {
    setBusyKey(preset.key);
    try {
      const { data } = await api.post(`/tenant/campaign-presets/${preset.key}/install`);
      if (data.already_installed) toast.info(`${preset.name} já estava instalada.`);
      else toast.success(`${preset.name} adicionada às suas mensagens!`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyKey(null); }
  };

  if (loading) {
    return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Campanhas prontas</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          Modelos de campanha para sua marca
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Mensagens testadas para o seu segmento. Clique em &quot;Instalar&quot; e elas aparecerão em <Link to={`/t/${slug}/mensagens`} className="underline">Mensagens</Link>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <Card key={p.key} data-testid={`preset-${p.key}`} className="p-5 rounded-tenant border-border flex flex-col">
            <div className="flex items-start gap-3 mb-3">
              <div className="text-3xl shrink-0">{p.icon}</div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold leading-tight">{p.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{p.channel}</div>
              </div>
              {p.installed && (
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30 gap-1">
                  <Check className="w-3 h-3" /> Instalada
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-3">{p.description}</p>
            <div className="bg-muted/40 rounded-lg p-3 text-xs italic text-muted-foreground mb-4 leading-relaxed flex-1">
              &ldquo;{p.body}&rdquo;
            </div>
            <div className="flex items-center justify-between gap-2 pt-3 border-t border-border">
              {p.trigger && (
                <Badge variant="outline" className="gap-1 text-[10px]"><Sparkles className="w-3 h-3" /> Auto: {p.trigger}</Badge>
              )}
              <Button
                size="sm"
                disabled={p.installed || busyKey === p.key}
                onClick={() => install(p)}
                data-testid={`preset-install-${p.key}`}
                className="ml-auto gap-1.5"
                style={!p.installed ? { background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` } : undefined}
                variant={p.installed ? "outline" : "default"}
              >
                {busyKey === p.key && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {p.installed ? "Instalada" : <>Instalar <ArrowRight className="w-3.5 h-3.5" /></>}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
